import { onScopeDispose, toValue, watch, type MaybeRefOrGetter } from "vue";

type EscapeEvent = Pick<
  KeyboardEvent,
  | "key"
  | "repeat"
  | "isComposing"
  | "defaultPrevented"
  | "preventDefault"
  | "stopImmediatePropagation"
>;

/** One physical key press dismisses only the most recently opened layer. */
export function createEscapeStack() {
  const layers: { id: symbol; dismiss: () => void }[] = [];
  return {
    add(dismiss: () => void) {
      const layer = { id: Symbol("escape-layer"), dismiss };
      layers.push(layer);
      return () => {
        const index = layers.indexOf(layer);
        if (index >= 0) layers.splice(index, 1);
      };
    },
    handle(event: EscapeEvent) {
      if (event.key !== "Escape" || event.isComposing || event.defaultPrevented)
        return;
      const top = layers.at(-1);
      if (!top) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!event.repeat) top.dismiss();
    },
  };
}

const stack = createEscapeStack();
let subscribers = 0;

/** Register a layer's open state; changing a nonempty key brings it to the top. */
export function useEscapeLayer(
  active: MaybeRefOrGetter<unknown>,
  dismiss: () => void,
) {
  if (typeof document !== "undefined" && subscribers++ === 0)
    document.addEventListener("keydown", stack.handle, true);
  let remove: (() => void) | undefined;
  const stop = watch(
    () => toValue(active),
    (value) => {
      remove?.();
      remove = value ? stack.add(dismiss) : undefined;
    },
    { immediate: true, flush: "sync" },
  );
  onScopeDispose(() => {
    stop();
    remove?.();
    if (typeof document !== "undefined" && --subscribers === 0)
      document.removeEventListener("keydown", stack.handle, true);
  });
}
